import request from "./httpService";

const SMS_ENDPOINT = '/sms';

/**
 * Send SMS to customer
 * Automatically handles token replacement on backend
 * @param {Object} data - SMS data
 * @param {number} data.customerId - Customer ID
 * @param {string} data.toNumber - Recipient phone number (E.164 format)
 * @param {string} data.message - Message body (can contain tokens like {patient_name})
 * @param {number} data.appointmentId - Optional: Appointment ID for token context
 * @param {number} data.invoiceId - Optional: Invoice ID for token context
 * @param {number} data.orderId - Optional: Order ID for token context
 * @param {string} data.communicationType - Optional: 'appointment_reminder', 'general', etc.
 * @returns {Promise} Response with messageId, provider, cost
 */
const sendSms = (data) => {
    return request({
        method: "POST",
        url: `${SMS_ENDPOINT}/send`,
        data: data,
    });
};

/**
 * Send test SMS to verify SMS provider configuration
 * @param {string} toNumber - Optional: Test phone number (uses configured testNumber if not provided)
 * @returns {Promise} Response with message, messageId, toNumber
 */
const sendTestSms = (toNumber = null) => {
    return request({
        method: "POST",
        url: `${SMS_ENDPOINT}/test`,
        data: toNumber ? { toNumber } : {},
    });
};

/**
 * Get SMS provider status
 * @returns {Promise} Provider status info (account balance, status, etc.)
 */
const getProviderStatus = () => {
    return request({
        method: "GET",
        url: `${SMS_ENDPOINT}/status`,
    });
};

/**
 * Validate bulk SMS filters and get recipient counts
 * @param {Object} data - Filter configuration
 * @param {string} data.filterType - 'appointments_tomorrow' | 'appointments_next_7_days' | 'appointments_custom_range'
 * @param {string} data.filterStartDate - Optional: start date for custom range (YYYY-MM-DD)
 * @param {string} data.filterEndDate - Optional: end date for custom range (YYYY-MM-DD)
 * @param {number} data.storeId - Optional: location/store ID (null = all stores)
 * @returns {Promise} { totalCount, optedOutCount, validCount }
 */
const validateBulkFilters = (data) => {
    return request({
        method: "POST",
        url: `${SMS_ENDPOINT}/bulk/validate`,
        data: data,
    });
};

/**
 * Create and queue a bulk SMS send
 * @param {Object} data - Bulk send configuration
 * @param {string} data.filterType - Type of filter
 * @param {string} data.filterStartDate - Optional: start date
 * @param {string} data.filterEndDate - Optional: end date
 * @param {number} data.templateId - SMS template ID (required OR message)
 * @param {string} data.message - Custom message body (optional if using template)
 * @param {number} data.storeId - Optional: location/store ID
 * @returns {Promise} { batchId, status, totalRecipients, optedOutCount }
 */
const createBulkSend = (data) => {
    return request({
        method: "POST",
        url: `${SMS_ENDPOINT}/bulk/send`,
        data: data,
    });
};

/**
 * Get bulk SMS batch progress/status
 * Poll this endpoint to track sending progress
 * @param {string} batchId - Batch UUID
 * @returns {Promise} { batchId, status, totalRecipients, sentCount, blockedCount, failedCount, completedAt }
 */
const getBatchProgress = (batchId) => {
    return request({
        method: "GET",
        url: `${SMS_ENDPOINT}/bulk/batch/${batchId}/status`,
    });
};

/**
 * Get bulk SMS batch results
 * @param {string} batchId - Batch UUID
 * @param {boolean} includeLists - Include detailed sent/blocked/failed lists
 * @returns {Promise} Batch results with counts and optional customer lists
 */
const getBatchResults = (batchId, includeLists = false) => {
    return request({
        method: "GET",
        url: `${SMS_ENDPOINT}/bulk/batch/${batchId}/results`,
        params: { includeLists: includeLists ? 'true' : 'false' }
    });
};

/**
 * Send bulk appointment reminders for a specific date and location
 * Simplified bulk send for day-specific reminders
 * @param {Object} data - Reminder configuration
 * @param {string} data.date - Date in YYYY-MM-DD format
 * @param {number} data.locationId - Location/store ID
 * @param {number} data.templateId - SMS template ID
 * @returns {Promise} { batchId, status, totalRecipients, optedOutCount }
 */
const sendDayReminders = (data) => {
    return request({
        method: "POST",
        url: `${SMS_ENDPOINT}/bulk/send-day-reminders`,
        data: data,
    });
};

export const SmsService = {
    sendSms,
    sendTestSms,
    getProviderStatus,
    validateBulkFilters,
    createBulkSend,
    getBatchProgress,
    getBatchResults,
    sendDayReminders
};
